import { createAnalytics } from "@trustpilot/businessapp-analytics";
import React from "react";

import { getGroupTraits } from "../api";
import configuration from "../configuration";
import { Analytics, BaseDataResponseBody, GroupTraitsResponseBody } from "../types";
import { getAutomationStatus } from "./automationStatus";
import { isPaidDomain } from "./isPaidDomain";

declare global {
  type HotjarCommand = "event" | "identify" | "trigger" | "tagRecording";
  type HotjarStatic = (command: HotjarCommand, ...commandArgs: unknown[]) => void;
  const hj: HotjarStatic;
}

type HotJarProperties = Record<string, string | number | boolean>;

const integrations = {
  marketing: ["Google Tag Manager"],
  nonMarketing: [
    "Segment.io",
    "Amplitude",
    "Amplitude Actions",
    "Hotjar",
    `bigquery-segment-${configuration.Environment.toLowerCase()}`,
  ],
};

/**
 * Creates a link between the Segment/Amplitude user and Hotjar user.
 * It will allow users responding to Hotjar surveys to be found
 * in Amplitude to analyze their journey on site.
 * https://help.hotjar.com/hc/articles/360033640653
 *
 * The segment_user_id is preferable as it is used across our systems as
 * an identifier, but it doesn't work for anonymous users and Amplitude
 * does not have the concept of an anonymous user ID, thus we also record
 * the device ID which can be found/searched in Amplitude.
 *
 * The segment_anonymous_id can be used anywhere else.
 */
const hotJarIdentify = (hotJarProperties: HotJarProperties) => {
  window.analytics.ready(() => {
    if (typeof hj === "function") {
      hj("identify", null, {
        // eslint-disable-next-line camelcase
        amplitude_device_id: window.amplitude?.getInstance().options.deviceId,
        // eslint-disable-next-line camelcase
        segment_anonymous_id: window.analytics.user().anonymousId(),
        // eslint-disable-next-line camelcase
        segment_user_id: window.analytics.user().id(),
        ...hotJarProperties,
      });
    }
  });
};

const instrumentGroup = (
  analytics: Analytics,
  businessUnit: BaseDataResponseBody["businessUnit"],
  additionalGroupTraits: GroupTraitsResponseBody,
) => {
  const {
    id,
    country,
    numberOfReviews,
    displayName,
    planName,
    availableFeatures,
    businessModules,
    name: { identifying },
    score: { trustScore, stars },
  } = businessUnit;

  const accountLevelTraits = {
    groupType: "businessUnit",
    groupValue: id,
    id,
    country,
    trustScore,
    stars,
    displayName,
    reviewCount: numberOfReviews.total,
    domain: identifying,
    tierName: planName,
    modules: businessModules.map(({ name }) => name),
    isPaying: isPaidDomain(availableFeatures),
    ...additionalGroupTraits,
  };

  analytics.group(accountLevelTraits);
};

const Context = React.createContext<Analytics>(null);

export const AnalyticsProvider: React.FC<{
  businessUser: BaseDataResponseBody["businessUser"];
  businessUnit: BaseDataResponseBody["businessUnit"];
  locale: string;
  children?: React.ReactNode;
}> = ({ businessUser, businessUnit, locale, children }) => {
  const businessModules = businessUnit?.businessModules?.map(({ name }) => name);
  const isPaying = isPaidDomain(businessUnit?.availableFeatures);
  const businessUnitTier = {
    name: businessUnit?.planName,
    expireDate: businessUnit?.planExpireDate,
  };

  const [analytics, setAnalytics] = React.useState<Analytics>({
    identify: () => Promise.resolve(),
    group: () => Promise.resolve(),
    track: () => Promise.resolve(),
    pageView: () => Promise.resolve(),
    hasInit: false,
  });

  React.useEffect(() => {
    if (analytics.hasInit) {
      return;
    }

    setAnalytics({
      ...createAnalytics(
        {
          activationDate: businessUser.activationDate,
          country: {
            country: businessUser.country.country,
          },
          email: businessUser.email,
          name: businessUser.name,
          id: businessUser.id,
        },
        {
          id: businessUnit?.id,
          name: {
            identifying: businessUnit?.name?.identifying,
          },
          displayName: businessUnit?.displayName,
          access: {
            role: {
              name: businessUnit?.access?.role?.name,
            },
          },
          businessModules,
          isPaying,
          tier: businessUnitTier,
        },
        locale,
        {
          segmentIoKey: configuration.SegmentIoKey,
          integrations,
        },
      ),
      hasInit: true,
    });
  }, []);

  React.useEffect(() => {
    if (!analytics.hasInit) {
      return;
    }

    const identifyAndGroup = async () => {
      analytics.identify();

      if (businessUnit) {
        const additionalGroupTraits = await getGroupTraits({ businessUnitId: businessUnit.id });
        instrumentGroup(analytics, businessUnit, additionalGroupTraits);
      }
    };

    const getAutomationAndIdentify = async () => {
      if (businessUnit && locale) {
        const automationStatus = await getAutomationStatus(businessUnit.id, locale);
        hotJarIdentify({
          country: businessUnit.country,
          locale: locale,
          totalNumberOfReviews: businessUnit.numberOfReviews.total,
          hasReviews: businessUnit.numberOfReviews.total > 0,
          userAccess: businessUnit.access.role.name,
          planName: businessUnit.planName,
          hasAccountManagementConsent: businessUnit.hasAccountManagementConsent,
          trustScore: businessUnit.score.trustScore,
          stars: businessUnit.score.stars,
          ...automationStatus,
        });
      }
    };

    identifyAndGroup();
    getAutomationAndIdentify();
  }, [analytics.hasInit]);

  return <Context.Provider value={analytics}>{analytics.hasInit && children}</Context.Provider>;
};

export const useAnalytics = () => React.useContext(Context);
